import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SortingState } from '@tanstack/react-table';
import { Input } from '@/components/ui/input';
import { DataTable } from 'components/table/DataTable';
import { InvoiceColumns } from './InvoiceColumns';
import { Pagination, Sort } from '@libs/services';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { InvoiceStatus, InvoiceWithUserData } from 'src/trpc';

export const InvoiceDataTable: FC<{
  invoiceData: { invoices: InvoiceWithUserData[]; total?: number };
  setParams: (params: Pagination & Sort) => void;
  setSearch?: (search: string) => void;
  pagination: Pagination;
  sortingObj: Sort;
  search?: string;
  status: InvoiceStatus | '';
  setStatus: (status: InvoiceStatus | '') => void;
}> = ({
  invoiceData,
  setParams,
  search = '',
  setSearch,
  pagination: { offset = 0, limit = 10 },
  sortingObj: { sortDirection = 'asc', sortKey = '' },
  status,
  setStatus,
}) => {
  const navigate = useNavigate();

  const currentPage = offset === 0 ? 1 : offset / limit + 1;

  const [sorting, setSorting] = useState<SortingState>([]);

  useEffect(() => {
    if (sorting.length) {
      setParams({
        offset: 0,
        limit: 10,
        sortDirection: sorting[0].desc ? 'desc' : 'asc',
        sortKey: sorting[0].id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting]);

  // Ensure invoiceData has the expected structure
  const invoices = invoiceData?.invoices || [];
  const total = invoiceData?.total ?? 0;

  return (
    <div className="flex flex-col w-full gap-6">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Label htmlFor="status-filter" className="label text-xs">
            Search invoices
          </Label>
          <Input
            value={search}
            onChange={(e) => {
              if (setSearch) {
                setSearch(e?.target?.value);
              }
            }}
            placeholder="Search invoice by customer name, email, phone number, or invoice number"
          />
        </div>
        <div className="w-full sm:w-48">
          <Label htmlFor="status-filter" className="label text-xs">
            Status
          </Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger id="status-filter">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="payment-failed">Payment Failed</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="partially_paid">Partially Paid</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <DataTable
        total={total}
        resourceName="invoices"
        offset={offset}
        limit={limit}
        columns={InvoiceColumns}
        currentPage={currentPage}
        data={invoices as InvoiceWithUserData[]}
        sorting={sorting}
        onSortingChange={setSorting}
        hasNextPage={total > offset + limit}
        hasPreviousPage={offset > 0}
        onRowClick={(row) => navigate(`/invoices/${row._id}`)}
        onPageChange={(change) => {
          setParams({ offset: offset + change * limit, limit, sortDirection, sortKey });
        }}
        pageCount={Math.ceil(total / limit)}
      />
    </div>
  );
};
